import { ServiceFee } from '@/apis/providers/requests'
import { Category } from '@/apis/providers/types/provider.types'
import { Button } from '@/app/common/components/Button'
import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import { CurrencyInputField } from '@/app/common/components/currency-input/currency-input'
import FormErrorMessage from '@/app/common/components/FormErrorMessage'
import { cn, currencyFormatter } from '@/app/common/utilities'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

interface Props {
  paymentAmount: number
  onSuccess: (data: Category[]) => void
  defaultValues: Category[]
  serviceFee: ServiceFee
}

// Create a schema for form validation
const createAllocationSchema = (totalAmount: number) => {
  const schema = z
    .object({
      allocations: z.array(
        z.object({
          id: z.number(),
          amount: z.number().min(0, 'Amount cannot be negative'),
          name: z.string(),
        })
      ),
    })
    .refine(
      (data) => {
        const total = data.allocations.reduce(
          (sum, item) => sum + item.amount,
          0
        )
        return Math.abs(total - totalAmount) < 0.01 // Allow for small floating point errors
      },
      {
        message: `Total allocation must equal ${currencyFormatter.format(totalAmount)}`,
        path: ['allocations'],
      }
    )
    .refine(
      (data) => {
        const allValuesEntered = data.allocations.every(
          (allocation) => allocation.amount > 0
        )
        return allValuesEntered
      },
      {
        message: 'All categories must have a value',
        path: ['allocations'],
      }
    )

  return schema
}

function ExpenseAllocationForm(props: Props) {
  const { paymentAmount, onSuccess, defaultValues } = props
  const { t } = useTranslation()
  const allocationSchema = createAllocationSchema(paymentAmount)
  type AllocationFormValues = z.infer<typeof allocationSchema>

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AllocationFormValues>({
    resolver: zodResolver(allocationSchema),
    defaultValues: {
      allocations: defaultValues,
    },
    mode: 'onChange',
  })

  // Watch the allocations to calculate remaining and allocated amounts
  const watchedAllocations = watch('allocations')
  // Calculate these values directly in the render phase
  const allocatedAmount = watchedAllocations
    ? parseFloat(
        watchedAllocations
          .reduce((sum, allocation) => sum + (allocation.amount || 0), 0)
          .toFixed(2)
      )
    : 0

  const remainingAmount = parseFloat(
    (paymentAmount - allocatedAmount).toFixed(2)
  )

  // Submit handler
  const onSubmit = (data: AllocationFormValues) => {
    onSuccess(data.allocations)
  }

  return (
    <Card className="">
      <CardHeader>
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">{t('allocation.expense_Allocation')}</h2>
          <div className="text-sm">
            Total:
            <span className="font-medium">
              {currencyFormatter.format(paymentAmount)}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Allocation Summary */}
        <div className="grid grid-cols-2 gap-4 text-sm"></div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {errors.allocations?.message && (
            <div className="p-3 rounded-md bg-red-100 text-red-700 text-sm">
              {errors.allocations.message}
            </div>
          )}

          {defaultValues.map((allocation, index) => {
            return (
              <div key={allocation.id} className="flex items-center space-x-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {allocation?.name || `Category ${allocation.id}`}
                  </label>

                  <Controller
                    control={control}
                    name={`allocations.${index}.amount`}
                    render={({ field }) => (
                      <div className="relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <CurrencyInputField
                          id={`allocations.${index}.amount`}
                          onChange={(value) => {
                            field.onChange(value)
                          }}
                          value={field.value}
                        />
                      </div>
                    )}
                  />
                </div>
              </div>
            )
          })}
          <FormErrorMessage message={errors?.allocations?.root?.message} />

          <div className="pt-5">
            <div className="space-y-1">
              <div
                className={cn('text-amber-700 text-sm font-medium', {
                  'text-emerald-600': remainingAmount === 0,
                })}
              >
                {t('allocation.fill_all_fields', {
                  amount: currencyFormatter.format(remainingAmount),
                })}
              </div>
              <div className="text-gray-600 text-sm font-medium">
                {currencyFormatter.format(allocatedAmount)} /{' '}
                {currencyFormatter.format(paymentAmount)}
              </div>
            </div>
            <div className="flex space-x-4">
              <Button className="w-full mt-5" size={'lg'} type="submit">
                {t('continue')}
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default ExpenseAllocationForm
